import React, { useMemo, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import clsx from "clsx";
import { useTheme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import IconButton from "@material-ui/core/IconButton";
import LinearProgress from "@material-ui/core/LinearProgress";
import Tooltip from "@material-ui/core/Tooltip";
import InputBase from "@material-ui/core/InputBase";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ViewListIcon from "@material-ui/icons/ViewList";
import ViewModuleIcon from "@material-ui/icons/ViewModule";
import {
  GridColDef,
  GridCellParams,
  GridCellEditCommitParams,
  GridSelectionModel,
  GridValueFormatterParams,
  GridToolbarFilterButton,
} from "@material-ui/x-grid";

import type { TransactionWithDetails } from "src/interfaces/transaction.interface";
import XGrid from "src/components/StyledXGrid";
import VendorField from "src/components/VendorField";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import { updateTransactionVendor } from "src/apiService/modules/transactions";
import { formatCurrencyValue } from "src/utils";
import { formatDate } from "src/utils/formatter";
import makeRenderCel from "src/utils/makeRenderCell";
import { makeTransactionsStyle } from "src/utils/gridCellStyles";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import { updateTransactionDescriptionOptimistic } from "src/store/transaction/actions";
import { useCardStyles, useTransactionXGridStyles } from "./styles";

export interface TransactionGroup {
  accountId: string;
  accountName: string;
  transactions: TransactionWithDetails[];
  total: number;
}

export type ApprovalState = "idle" | "loading" | "success";

interface TransactionCategoryCardProps {
  group: TransactionGroup;
  expanded: boolean;
  completed: boolean;
  approvalState?: ApprovalState;
  approvedCount?: number;
  selectedIds: string[];
  onToggleExpand: () => void;
  onToggleSelect: (id: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onApprove: (transactionIds?: string[]) => void;
  onRecategorize: () => void;
  onVendorAssign: () => void;
  onDelete: () => void;
  onCollapseComplete?: () => void;
  loading?: boolean;
  isFirstCard?: boolean;
}

export type ViewMode = "card" | "table";

// Custom toolbar with Select All checkbox, quick filter, and view toggle
interface TransactionToolbarProps {
  totalCount: number;
  selectedCount: number;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  quickFilter?: string;
  onQuickFilterChange?: (value: string) => void;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
}

function TransactionTableToolbar(props: TransactionToolbarProps) {
  const recordEvent = useRecordEvent();

  const {
    totalCount,
    selectedCount,
    onSelectAll,
    onDeselectAll,
    quickFilter,
    onQuickFilterChange,
    viewMode = "card",
    onViewModeChange,
  } = props;
  const xGridClasses = useTransactionXGridStyles();
  const allSelected = selectedCount === totalCount && totalCount > 0;
  const someSelected = selectedCount > 0 && selectedCount < totalCount;

  const handleToggle = () => {
    if (allSelected || someSelected) {
      onDeselectAll();
    } else {
      onSelectAll();
    }
  };

  return (
    <Box display="flex" alignItems="center" px={1} py={1}>
      {/* Card view: show checkbox + label. Table view: show filter button */}
      {viewMode === "card" ? (
        <Box display="flex" alignItems="center" mr={2}>
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={handleToggle}
            color="primary"
            size="small"
          />
          <Typography
            variant="body2"
            color="textSecondary"
            className={xGridClasses.selectAllLabel}
            onClick={handleToggle}
          >
            {allSelected
              ? "Deselect All"
              : someSelected
              ? `${selectedCount} selected`
              : "Select All"}
          </Typography>
        </Box>
      ) : (
        <Box display="flex" alignItems="center" mr={1}>
          <GridToolbarFilterButton />
        </Box>
      )}
      <Box flex={1}>
        <TextField
          placeholder="Search transactions..."
          value={quickFilter || ""}
          onChange={(e) => onQuickFilterChange?.(e.target.value)}
          onBlur={(e) => {
            const text = e.target.value;
            if (!text) return;
            recordEvent({
              type: "feature-adoption",
              feature: "quick-filter",
              element: "approve-transactions",
              text,
            });
          }}
          variant="outlined"
          fullWidth
        />
      </Box>
      <Box display="flex" alignItems="center" ml={1}>
        <Tooltip title="Card view">
          <IconButton
            size="small"
            onClick={() => {
              onViewModeChange?.("card");
              recordEvent({
                type: "feature-adoption",
                feature: "data-view-toggle",
                element: "approve-transactions",
                view: "card",
              });
            }}
            className={clsx(
              xGridClasses.viewModeButton,
              viewMode === "card" && xGridClasses.viewModeButtonActive
            )}
          >
            <ViewModuleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Table view">
          <IconButton
            size="small"
            onClick={() => {
              onViewModeChange?.("table");
              recordEvent({
                type: "feature-adoption",
                feature: "data-view-toggle",
                element: "approve-transactions",
                view: "table",
              });
            }}
            className={clsx(
              xGridClasses.viewModeButton,
              viewMode === "table" && xGridClasses.viewModeButtonActive
            )}
          >
            <ViewListIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

// Transaction card cell component - combines all data into one visual unit
function TransactionCardCell({
  transaction,
  timeZone,
}: {
  transaction: TransactionWithDetails;
  timeZone: string;
}) {
  const xGridClasses = useTransactionXGridStyles();
  const date = formatDate(transaction.date, "MMM D, YYYY", timeZone);
  const glAccount = transaction.account || "Uncategorized";
  const bankAccount = transaction.integration_name || "";
  const vendor = transaction.vendorName || "";
  const chargeDescription =
    transaction.description || transaction.name || "No description";
  const amount = formatCurrencyValue(Math.abs(transaction.amount));
  const isNegative = transaction.amount < 0;

  const bankVendorParts = [bankAccount, vendor].filter(Boolean);
  const bankVendorLine = bankVendorParts.join(" • ");

  return (
    <div className={xGridClasses.cellContainer}>
      <Typography
        variant="caption"
        color="textSecondary"
        display="block"
        className={xGridClasses.cellDate}
      >
        {date}
      </Typography>
      <div className={xGridClasses.cellRow}>
        <Typography variant="subtitle2" color="primary">
          {glAccount}
        </Typography>
        <Typography
          variant="subtitle2"
          className={isNegative ? xGridClasses.cellAmountNegative : undefined}
        >
          {amount}
        </Typography>
      </div>
      {bankVendorLine && (
        <Typography variant="caption" color="textSecondary" display="block">
          {bankVendorLine}
        </Typography>
      )}
      <Typography variant="body2" color="textPrimary">
        {chargeDescription}
      </Typography>
    </div>
  );
}

// XGrid column - single column rendering card-style cells (Card View)
function buildCardViewColumns(timeZone: string): GridColDef[] {
  return [
    {
      field: "transaction",
      headerName: "Transaction",
      flex: 1,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const transaction = params.row as TransactionWithDetails;
        return (
          <TransactionCardCell transaction={transaction} timeZone={timeZone} />
        );
      },
    },
    // Hidden columns for quick filter search
    {
      field: "name",
      headerName: "Name",
      hide: true,
    },
    {
      field: "description",
      headerName: "Description",
      hide: true,
    },
    {
      field: "vendor",
      headerName: "Vendor",
      hide: true,
    },
    {
      field: "amount",
      headerName: "Amount",
      hide: true,
    },
  ];
}

function DescriptionEditCell(params: GridCellParams) {
  const { id, value, api, field } = params;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const input = inputRef.current;
    if (input) {
      input.focus();
      input.select();
    }
  }, []);

  const navigateToRow = (offset: number) => {
    api.commitCellChange({ id, field });
    api.setCellMode(id, field, "view");
    const currentIdx = api.getRowIndex(id);
    const rowCount = api.getRowsCount();
    const targetIdx = currentIdx + offset;
    if (targetIdx >= 0 && targetIdx < rowCount) {
      const targetId = api.getRowIdFromRowIndex(targetIdx);
      api.scrollToIndexes({ rowIndex: targetIdx });
      api.setCellMode(targetId, field, "edit");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab" || e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      const goBack = e.key === "ArrowUp" || (e.key === "Tab" && e.shiftKey);
      navigateToRow(goBack ? -1 : 1);
    }
  };

  return (
    <InputBase
      inputRef={inputRef}
      className="MuiDataGrid-editInputCell"
      fullWidth
      defaultValue={String(value || "")}
      onChange={(e) => {
        api.setEditCellValue({ id, field, value: e.target.value });
      }}
      onKeyDown={handleKeyDown}
    />
  );
}

function VendorCellEditor({
  transaction,
}: {
  transaction: TransactionWithDetails;
}) {
  const handleChange = (vendorId?: string) => {
    updateTransactionVendor(transaction.id, vendorId);
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      style={{ width: "100%" }}
    >
      <VendorField
        value={transaction.vendor}
        onChange={handleChange}
        label={null}
        size="small"
        variant="standard"
        openOnFocus
      />
    </div>
  );
}

// XGrid columns for Table View - traditional table layout (consistent with transaction details)
function buildTableViewColumns(timeZone: string): GridColDef[] {
  return [
    {
      field: "date",
      headerName: "Date",
      width: 110,
      type: "date",
      valueFormatter: (params: GridValueFormatterParams) => {
        return formatDate(params.value as Date, "MMM D, YYYY", timeZone);
      },
    },
    {
      field: "name",
      headerName: "Description",
      flex: 1,
      minWidth: 150,
      type: "string",
      editable: true,
      renderCell: (params: GridCellParams) => {
        const transaction = params.row as TransactionWithDetails;
        return transaction.description || transaction.name || "No description";
      },
      renderEditCell: (params: GridCellParams) => (
        <DescriptionEditCell {...params} />
      ),
      valueGetter: (params) => {
        const transaction = params.row as TransactionWithDetails;
        return transaction.description || transaction.name || "";
      },
    },
    {
      field: "vendorName",
      headerName: "Vendor",
      width: 180,
      type: "string",
      renderCell: (params: GridCellParams) => {
        const transaction = params.row as TransactionWithDetails;
        return <VendorCellEditor transaction={transaction} />;
      },
    },
    {
      field: "integration_name",
      headerName: "Bank Account",
      width: 160,
      type: "string",
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 100,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: makeRenderCel({
        valueFormatter: (params) => {
          let value = params.value as number;
          if (value < 0) value = Math.abs(value);
          return formatCurrencyValue(value);
        },
        style: makeTransactionsStyle,
      }),
    },
  ];
}

// Helper to escape regex special characters
function escapeRegExp(value: string) {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
}

function TransactionCategoryCard({
  group,
  expanded,
  completed,
  approvalState = "idle",
  approvedCount = 0,
  selectedIds,
  onToggleExpand,
  onToggleSelect,
  onSelectAll,
  onDeselectAll,
  onApprove,
  onRecategorize,
  onVendorAssign,
  onDelete,
  onCollapseComplete,
  loading = false,
  isFirstCard = false,
}: TransactionCategoryCardProps) {
  const recordEvent = useRecordEvent();
  const dispatch = useDispatch();

  const classes = useCardStyles();
  const xGridClasses = useTransactionXGridStyles();
  const theme = useTheme();
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const cardViewColumns = useMemo(
    () => buildCardViewColumns(timeZone),
    [timeZone]
  );
  const tableViewColumns = useMemo(
    () => buildTableViewColumns(timeZone),
    [timeZone]
  );
  const [quickFilter, setQuickFilter] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("card");
  const prevExpandedRef = useRef(expanded);
  const cardRef = useRef<HTMLDivElement>(null);
  const transitionContainerRef = useRef<HTMLDivElement>(null);

  // Track if XGrid should be rendered (delayed unmount)
  const [shouldRenderXGrid, setShouldRenderXGrid] = useState(expanded);

  useEffect(() => {
    if (expanded) {
      setShouldRenderXGrid(true);
      const scrollTimer = setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
      return () => clearTimeout(scrollTimer);
    } else if (prevExpandedRef.current) {
      const container = transitionContainerRef.current;
      if (container) {
        const handleTransitionEnd = (e: TransitionEvent) => {
          if (e.target !== container) return;
          container.removeEventListener("transitionend", handleTransitionEnd);
          setShouldRenderXGrid(false);
          setQuickFilter("");
          onCollapseComplete?.();
        };
        container.addEventListener("transitionend", handleTransitionEnd);
        return () =>
          container.removeEventListener("transitionend", handleTransitionEnd);
      }
    }
    prevExpandedRef.current = expanded;
  }, [expanded, onCollapseComplete]);

  // Compute filtered transactions based on quick filter (XGrid handles column filtering in table view)
  const filteredTransactions = useMemo(() => {
    if (!quickFilter) return group.transactions;
    const searchRegex = new RegExp(escapeRegExp(quickFilter), "i");
    return group.transactions.filter((t) => {
      const searchFields = [
        t.name,
        t.description,
        t.vendor,
        t.vendorName,
        t.account,
        t.integration_name,
        t.amount?.toString(),
      ];
      return searchFields.some(
        (field) => field && searchRegex.test(field.toString())
      );
    });
  }, [group.transactions, quickFilter]);

  // Get IDs of filtered (visible) transactions
  const filteredIds = useMemo(
    () => filteredTransactions.map((t) => t.id),
    [filteredTransactions]
  );

  const selectedCount = useMemo(() => {
    return filteredTransactions.filter((t) => selectedIds.includes(t.id))
      .length;
  }, [filteredTransactions, selectedIds]);

  const handleSelectAllVisible = () => {
    filteredIds.forEach((id) => {
      if (!selectedIds.includes(id)) {
        onToggleSelect(id);
      }
    });
  };

  const handleDeselectAllVisible = () => {
    filteredIds.forEach((id) => {
      if (selectedIds.includes(id)) {
        onToggleSelect(id);
      }
    });
  };

  const formattedTotal = formatCurrencyValue(Math.abs(group.total));

  const handleApproveAll = () => {
    const allIds = group.transactions.map((t) => t.id);
    onSelectAll();
    onApprove(allIds);
    recordEvent({
      type: "feature-adoption",
      feature: "bulk-approve",
      element: "compressed",
      count: group.transactions.length,
    });
  };

  const getApproveButtonText = () => {
    if (selectedCount === 0) {
      return "Select transactions";
    }
    if (selectedCount === group.transactions.length) {
      return `Approve All (${group.transactions.length})`;
    }
    return `Approve ${selectedCount} Selected`;
  };

  const handleCellEditCommit = async (params: GridCellEditCommitParams) => {
    if (params.field !== "name") return;

    try {
      await dispatch(
        updateTransactionDescriptionOptimistic(
          params.id as string,
          String(params.value || "")
        )
      );
    } catch (error) {
      toast.error("Failed to save description. Please try again.");
      console.error("Failed to update transaction description", error);
    }
  };

  // Handle XGrid selection changes
  const handleSelectionChange = (newSelection: GridSelectionModel) => {
    const currentSet = new Set(selectedIds);
    const newSet = new Set(newSelection as string[]);

    for (const id of newSelection) {
      if (!currentSet.has(id as string)) {
        onToggleSelect(id as string);
      }
    }

    for (const id of selectedIds) {
      if (!newSet.has(id)) {
        onToggleSelect(id);
      }
    }
  };

  if (completed) {
    return (
      <div className={clsx(classes.card, classes.cardCompleted)}>
        <div className={classes.cardHeader}>
          <div className={classes.cardHeaderLeft}>
            <span className={classes.cardTitle}>{group.accountName}</span>
            <span className={classes.cardTotal}>{formattedTotal} total</span>
          </div>
          <Typography
            className={clsx(classes.cardCount, classes.cardCountCompleted)}
          >
            0 Transactions
          </Typography>
        </div>
        <div className={classes.completedState}>
          <CheckCircleOutlineIcon className={classes.completedIcon} />
          <Typography variant="body2">All transactions approved</Typography>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={cardRef}
      className={clsx(
        classes.card,
        expanded && classes.cardExpanded,
        approvalState === "success" && classes.cardSuccess
      )}
    >
      <div className={classes.cardHeader}>
        <div className={classes.cardHeaderLeft}>
          <span className={classes.cardTitle}>{group.accountName}</span>
          <span className={classes.cardTotal}>{formattedTotal} total</span>
        </div>
        <span
          className={clsx(
            classes.cardCount,
            approvalState === "success" && classes.cardCountSuccess
          )}
        >
          {group.transactions.length} Transaction
          {group.transactions.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className={classes.cardContent}>
        {/* Toggle Button - Always visible */}
        <Button
          disableRipple
          className={clsx(
            classes.toggleButton,
            approvalState === "success" && classes.toggleButtonSuccess
          )}
          onClick={onToggleExpand}
          endIcon={
            <ExpandMoreIcon
              fontSize="small"
              className={clsx(
                classes.expandIcon,
                expanded && classes.expandIconExpanded
              )}
            />
          }
        >
          {expanded
            ? "Click to hide transactions"
            : "Click to view transactions"}
        </Button>

        {/* Expanded content with XGrid showing card-style or table cells */}
        <div
          ref={transitionContainerRef}
          className={clsx(
            classes.transactionListContainer,
            expanded && classes.transactionListContainerExpanded
          )}
        >
          <div className={classes.transactionListInner}>
            {/* Render XGrid with delayed unmount for smooth animation */}
            {shouldRenderXGrid && (
              <div
                className={clsx(
                  xGridClasses.gridContainer,
                  viewMode === "table" && xGridClasses.gridContainerTable
                )}
                data-tour={isFirstCard ? "transaction-checkboxes" : undefined}
              >
                <XGrid
                  key={viewMode}
                  rows={filteredTransactions}
                  columns={
                    viewMode === "card" ? cardViewColumns : tableViewColumns
                  }
                  checkboxSelection
                  disableColumnMenu={viewMode === "card"}
                  hideFooter
                  headerHeight={viewMode === "card" ? 0 : 36}
                  rowHeight={viewMode === "card" ? 100 : 36}
                  className={clsx(
                    xGridClasses.root,
                    viewMode === "table" && xGridClasses.tableViewRoot
                  )}
                  selectionModel={selectedIds}
                  onSelectionModelChange={handleSelectionChange}
                  onCellEditCommit={handleCellEditCommit}
                  onFilterModelChange={(model) => {
                    recordEvent({
                      type: "feature-adoption",
                      feature: "table-filter",
                      element: "approve-transactions",
                      filter: JSON.stringify(model),
                    });
                  }}
                  components={{
                    Toolbar: TransactionTableToolbar,
                  }}
                  componentsProps={{
                    toolbar: {
                      totalCount: filteredTransactions.length,
                      selectedCount,
                      onSelectAll: handleSelectAllVisible,
                      onDeselectAll: handleDeselectAllVisible,
                      quickFilter,
                      onQuickFilterChange: setQuickFilter,
                      viewMode,
                      onViewModeChange: setViewMode,
                    },
                  }}
                />
              </div>
            )}

            {shouldRenderXGrid && approvalState === "idle" && (
              <div className={classes.buttonGroup}>
                <Box
                  display="flex"
                  style={{ gap: 8 }}
                  data-tour={isFirstCard ? "action-buttons" : undefined}
                >
                  <Button
                    variant="contained"
                    onClick={onDelete}
                    disabled={selectedCount === 0 || loading}
                    size="small"
                    style={
                      selectedCount === 0 || loading
                        ? undefined
                        : {
                            backgroundColor: theme.palette.error.main,
                            color: theme.palette.common.white,
                          }
                    }
                  >
                    Delete
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onRecategorize}
                    disabled={selectedCount === 0 || loading}
                    size="small"
                  >
                    Recategorize
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onVendorAssign}
                    disabled={selectedCount === 0 || loading}
                    size="small"
                  >
                    Change Vendor
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      onApprove();
                      recordEvent({
                        type: "feature-adoption",
                        feature: "bulk-approve",
                        element: "expanded",
                        count: selectedCount,
                      });
                    }}
                    disabled={selectedCount === 0 || loading}
                    size="small"
                  >
                    {getApproveButtonText()}
                  </Button>
                </Box>
              </div>
            )}
            {shouldRenderXGrid && approvalState === "loading" && (
              <div className={classes.loadingContainer}>
                <LinearProgress className={classes.loadingBar} />
                <Typography className={classes.loadingText}>
                  Approving...
                </Typography>
              </div>
            )}
            {shouldRenderXGrid && approvalState === "success" && (
              <div className={classes.successContainer}>
                <CheckCircleIcon className={classes.successIcon} />
                <Typography className={classes.successText}>
                  {approvedCount} transaction
                  {approvedCount !== 1 ? "s" : ""} approved!
                </Typography>
              </div>
            )}
          </div>
        </div>

        {/* Compact Approve All Button - Only visible when collapsed and not approving */}
        {!expanded && (
          <div className={classes.approvalContainer}>
            {approvalState === "idle" && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleApproveAll}
                disabled={loading}
                fullWidth
                className={classes.approveAllButton}
              >
                Approve All
              </Button>
            )}
            {approvalState === "loading" && (
              <div className={classes.loadingContainer}>
                <LinearProgress className={classes.loadingBar} />
                <Typography className={classes.loadingText}>
                  Approving...
                </Typography>
              </div>
            )}
            {approvalState === "success" && (
              <div className={classes.successContainer}>
                <CheckCircleIcon className={classes.successIcon} />
                <Typography className={classes.successText}>
                  {approvedCount} transaction
                  {approvedCount !== 1 ? "s" : ""} approved!
                </Typography>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default TransactionCategoryCard;
